import { useMemo, useRef, useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight,
  ChevronLeft,
  Star,
  LayoutGrid,
  List as ListIcon,
  ChevronDown,
  Circle,
  SlidersHorizontal,
  X,
} from "lucide-react";
import { searchTours, isExactMatch, matchRegion, applyFilters, sortTours, withRealCounts, parseDayRange, matchDeparture, dedupeAliases, parseBudget, describeBudget } from "@/lib/search";

const placeName = (name) => name.replace(/ Tour Packages$/, "");

// Replace hardcoded "N tours" labels with real counts and hide cards that would open nothing.
const withCounts = (list, tours, countOf) =>
  list.map((x) => ({ ...x, tours: countOf(x) })).filter((x) => x.tours > 0);

const buildSections = (catalog) => {
  const t = catalog.tours;
  const byName = (x) => (isExactMatch(t, placeName(x.name)) ? searchTours(t, placeName(x.name)).length : 0);
  const mapObj = (obj) => Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, withCounts(v, t, byName)]).filter(([, v]) => v.length));
  return {
    primary: { ...catalog.primary, tabs: catalog.primary.tabs.map((tab) => ({ ...tab, data: withCounts(tab.data, t, byName) })) },
    regionSection: { ...catalog.regionSection, data: mapObj(catalog.regionSection.data) },
    season: { ...catalog.season, data: mapObj(catalog.season.data) },
    duration: { ...catalog.duration, cards: withCounts(catalog.duration.cards, t, (c) => { const [min, max] = parseDayRange(c.label); return t.filter((x) => x.days >= min && x.days <= max).length; }) },
    toursFrom: catalog.toursFrom && withCounts(dedupeAliases(catalog.toursFrom, "city").map((c) => ({ ...c, count: 0 })), t, (c) => t.filter((x) => matchDeparture(x, c.city)).length).map((c) => ({ ...c, count: c.tours })),
  };
};
import { Header } from "@/components/sections/Navbar";
import { Footer } from "@/components/sections/Footer";
import { FloatingWidgets } from "@/components/sections/FloatingWhatsApp";
import { FilterSidebar } from "@/components/india/FilterSidebar";
import { TourCard } from "@/components/india/TourCard";
import { CallbackCard } from "@/components/india/EnquiryForm";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import {
  PrimarySection,
  RegionSection,
  SeasonSection,
  InterestSection,
  DurationSection,
  PopularBlogs,
  Awards,
  TourReviews,
  ToursFromBox,
} from "@/components/india/IndiaSections";
import { getCatalog, REGION_TABS_BY_SCOPE } from "@/data/catalog";
import { INDIA_FAQS } from "@/data/indiaContent";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SORT_OPTIONS = [
  "Deals",
  "Price: High to Low",
  "Price: Low to High",
  "Duration: Long to Short",
  "Duration: Short to Long",
];

const makeInitialFilters = (facets) => ({
  price: [0, facets.priceMax],
  duration: null,
  packageType: "All",
  speciality: new Set(),
  departure: new Set(),
  joining: new Set(),
  dateFrom: "",
  dateTo: "",
  countries: new Set(),
  cities: new Set(),
});

const RegionTabs = ({ tabs, active, onChange }) => {
  const ref = useRef(null);
  const scroll = (d) => ref.current?.scrollBy({ left: d * 300, behavior: "smooth" });
  return (
    <div className="relative flex items-center gap-2">
      <button type="button" onClick={() => scroll(-1)} className="shrink-0 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-[var(--navy)] hover:bg-[var(--navy)] hover:text-white transition" aria-label="scroll left">
        <ChevronLeft className="w-4 h-4" />
      </button>
      <div ref={ref} className="flex gap-2.5 overflow-x-auto no-scrollbar py-1" data-testid="region-tabs">
        {tabs.map((t) => {
          const isActive = active === t.label;
          return (
            <button
              key={t.label}
              type="button"
              onClick={() => onChange(t.label)}
              data-testid={`region-tab-${t.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
              className={`shrink-0 rounded-full border px-4 py-2 text-[13px] font-medium whitespace-nowrap transition ${
                isActive
                  ? "border-[var(--navy)] text-[var(--navy)] bg-[#0b2545]/5 font-semibold"
                  : "border-gray-200 text-[#0b2545]/70 hover:border-[var(--navy)]"
              }`}
            >
              {t.label} ({t.count})
            </button>
          );
        })}
      </div>
      <button type="button" onClick={() => scroll(1)} className="shrink-0 w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-[var(--navy)] hover:bg-[var(--navy)] hover:text-white transition" aria-label="scroll right">
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default function PackagesPage({ scope = "india" }) {
  const catalog = useMemo(() => getCatalog(scope), [scope]);
  const facets = useMemo(() => withRealCounts(catalog.tours, catalog.facets), [catalog]);
  const sections = useMemo(() => buildSections(catalog), [catalog]);
  const regionTabs = useMemo(
    () => REGION_TABS_BY_SCOPE[scope].map((t) => ({ ...t, count: catalog.tours.filter((x) => matchRegion(x, t.label, catalog.allRegionLabel)).length })),
    [scope, catalog]
  );
  const [params, setParams] = useSearchParams();
  const q = params.get("q") || "";

  const [filters, setFilters] = useState(() => makeInitialFilters(facets));
  const [region, setRegion] = useState(catalog.allRegionLabel);
  const [sort, setSort] = useState("Deals");
  const [view, setView] = useState("list");
  const [readMore, setReadMore] = useState(false);
  const [mobileFilters, setMobileFilters] = useState(false);

  // Reset state when switching scope (india <-> world)
  useEffect(() => {
    setSort("Deals");
    setView("list");
    setReadMore(false);
  }, [scope]);

  // A new search (menu, header search, cards) or scope starts from a clean slate
  useEffect(() => {
    setFilters(makeInitialFilters(facets));
    setRegion(catalog.allRegionLabel);
    if (q) scrollToResults();
  }, [q, facets, catalog]);

  const searched = useMemo(() => (q ? searchTours(catalog.tours, q) : catalog.tours), [q, catalog]);
  const exact = useMemo(() => isExactMatch(catalog.tours, q), [q, catalog]);
  const results = useMemo(() => {
    const inRegion = searched.filter((t) => matchRegion(t, region, catalog.allRegionLabel));
    return sortTours(applyFilters(inRegion, filters, facets.priceMax), sort);
  }, [searched, filters, region, sort, catalog, facets]);

  const scrollToResults = () =>
    setTimeout(() => document.getElementById("results")?.scrollIntoView({ behavior: "smooth", block: "start" }), 60);

  const selectPlace = (name) => {
    setParams({ q: placeName(name) });
    scrollToResults();
  };
  const patchFilters = (patch) => {
    setFilters((f) => ({ ...f, ...patch(f) }));
    scrollToResults();
  };
  const pickDeparture = (city) =>
    patchFilters((f) => ({ departure: new Set([...f.departure, city]) }));
  const resetAll = () => {
    setFilters(makeInitialFilters(facets));
    setRegion(catalog.allRegionLabel);
    setParams({});
  };

  return (
    <div className="bg-white text-[var(--navy)] overflow-x-hidden">
      <Header />

      {/* Breadcrumb + Intro + Callback */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6 py-8">
          <nav className="text-[12px] text-gray-500 mb-4" data-testid="breadcrumb">
            <Link to="/" className="hover:text-[var(--navy)]">Home</Link>
            <span className="mx-1.5">&gt;</span>
            <span className="text-[var(--navy)] font-medium">{catalog.breadcrumb}</span>
          </nav>
          <div className="grid lg:grid-cols-[1fr_360px] gap-8 items-start">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="font-serif-display text-3xl md:text-[42px] text-[var(--navy)] leading-tight">{catalog.title}</h1>
              <p className={`text-[15px] text-gray-600 mt-3 leading-relaxed ${readMore ? "" : "line-clamp-2"}`}>{catalog.intro}</p>
              <button type="button" onClick={() => setReadMore((v) => !v)} className="mt-1 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--navy)] underline" data-testid="read-more-intro">
                {readMore ? "Read Less" : "Read More"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${readMore ? "rotate-180" : ""}`} />
              </button>
              <div className="mt-5 inline-flex items-center gap-2 bg-[var(--soft)] rounded-lg px-3.5 py-2">
                <span className="flex text-[var(--yellow-2)]">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="w-4 h-4 fill-[var(--yellow-2)]" />)}
                </span>
                <span className="font-bold text-[var(--navy)] text-sm">5*</span>
                <span className="w-px h-4 bg-gray-300" />
                <button type="button" className="text-[13px] font-semibold text-[var(--navy)] underline">{catalog.reviewsLabel}</button>
              </div>
            </motion.div>
            <div className="hidden lg:block sticky top-24">
              <CallbackCard />
            </div>
          </div>
        </div>
      </section>

      {/* Region tabs */}
      <section className="bg-white border-b border-gray-100 sticky top-0 z-30 py-3 shadow-sm">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <RegionTabs tabs={regionTabs} active={region} onChange={setRegion} />
        </div>
      </section>

      {/* Results toolbar + grid */}
      <section className="bg-[var(--soft)] py-8 scroll-mt-28" id="results">
        <div className="max-w-[1280px] mx-auto px-4 lg:px-6">
          <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
            <div>
              <div className="font-bold text-[var(--navy)] text-[17px]" data-testid="results-count">{results.length} {catalog.packagesNoun}</div>
              <div className="text-[13px] text-gray-500">Showing {results.length} of {catalog.tours.length} packages</div>
            </div>
            <div className="inline-flex items-center gap-2 bg-green-600 text-white text-[13px] font-semibold px-3.5 py-1.5 rounded-full" data-testid="ongoing-badge">
              <Circle className="w-2.5 h-2.5 fill-white animate-pulse" /> {catalog.ongoing}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setMobileFilters(true)}
                data-testid="mobile-filter-toggle"
                className="lg:hidden inline-flex items-center gap-1.5 border border-[var(--navy)] text-[var(--navy)] font-semibold text-[13px] px-3.5 py-2 rounded-md"
              >
                <SlidersHorizontal className="w-4 h-4" /> Filters
              </button>
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-gray-500">Sort by:</span>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger className="w-[180px] h-9 text-[13px] font-semibold" data-testid="sort-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SORT_OPTIONS.map((o) => <SelectItem key={o} value={o} className="text-[13px]">{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <button
                type="button"
                onClick={() => setView(view === "list" ? "grid" : "list")}
                data-testid="view-toggle"
                className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[var(--navy)] underline"
              >
                {view === "list" ? <><LayoutGrid className="w-4 h-4" /> Grid View</> : <><ListIcon className="w-4 h-4" /> List View</>}
              </button>
            </div>
          </div>

          {q && (
            <div className={`mb-5 rounded-xl border px-4 py-3 flex flex-wrap items-center gap-3 text-[13.5px] ${exact ? "bg-white border-gray-200" : "bg-[#fff8e7] border-[#f59e0b]/40"}`} data-testid="search-query-banner">
              <span className="text-[var(--navy)]">
                {exact ? <>Showing packages for <b>“{q}”</b></> : <>No exact {scope === "world" ? "World" : "India"} packages for <b>“{q}”</b> yet.{searched.length > 0 && " Showing the closest matches."}</>}
              </span>
              {parseBudget(q).label && (
                <button type="button" onClick={() => setParams(parseBudget(q).text ? { q: parseBudget(q).text } : {})} data-testid="clear-budget" className="inline-flex items-center gap-1 bg-[var(--yellow)] text-[var(--navy)] text-[12px] font-semibold pl-2.5 pr-2 py-1 rounded-full hover:bg-[var(--yellow-2)] transition-colors">
                  Budget: {describeBudget(parseBudget(q))} <X className="w-3 h-3" />
                </button>
              )}
              <button type="button" onClick={() => setParams({})} data-testid="clear-search-query" className="inline-flex items-center gap-1 bg-[var(--navy)] text-white text-[12px] font-medium pl-2.5 pr-2 py-1 rounded-full hover:bg-[var(--red)] transition-colors">
                {q} <X className="w-3 h-3" />
              </button>
              <Link to={`/search?q=${encodeURIComponent(q)}`} className="ml-auto text-[12.5px] font-semibold underline text-[var(--navy)]" data-testid="search-all-link">Search India &amp; World</Link>
            </div>
          )}
          <div className="grid lg:grid-cols-[300px_1fr] gap-6 items-start">
            <div className="hidden lg:block">
              <FilterSidebar filters={filters} setFilters={setFilters} facets={facets} scope={scope} onReset={() => setFilters(makeInitialFilters(facets))} />
            </div>
            <div>
              {sections.toursFrom && <ToursFromBox items={sections.toursFrom.slice(0, 12)} scope={scope} onPick={pickDeparture} />}
              {results.length === 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl p-16 text-center text-gray-500" data-testid="no-results">
                  No packages match your filters. <button className="text-[var(--navy)] underline font-semibold" onClick={resetAll} data-testid="no-results-reset">Reset filters</button>
                  <div className="mt-3 text-[13px]">Can't find it? <Link to="/contact" className="text-[var(--navy)] underline font-semibold">Ask our experts to plan a custom trip</Link></div>
                </div>
              ) : (
                <div className={view === "grid" ? "grid sm:grid-cols-2 gap-5" : "flex flex-col gap-5"} data-testid="results-list">
                  {results.map((t, i) => <TourCard key={t.slug} tour={t} view={view} index={i} />)}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile filters drawer */}
      <Sheet open={mobileFilters} onOpenChange={setMobileFilters}>
        <SheetContent side="left" className="w-[88%] max-w-sm p-0 pt-12 overflow-y-auto thin-scroll" data-testid="mobile-filter-sheet">
          <SheetTitle className="sr-only">Filter Your Tour</SheetTitle>
          <FilterSidebar filters={filters} setFilters={setFilters} facets={facets} scope={scope} onReset={() => setFilters(makeInitialFilters(facets))} />
        </SheetContent>
      </Sheet>

      {/* Discovery sections */}
      <PrimarySection key={`primary-${scope}`} config={sections.primary} onSelect={selectPlace} />
      <RegionSection key={`region-${scope}`} config={sections.regionSection} onSelect={selectPlace} />
      <SeasonSection key={`season-${scope}`} config={sections.season} onSelect={selectPlace} />
      <InterestSection key={`interest-${scope}`} config={catalog.interest} tours={catalog.tours} onShowAll={(tab) => patchFilters(() => ({ speciality: new Set([tab]) }))} />
      <DurationSection key={`duration-${scope}`} config={sections.duration} onSelect={(label) => patchFilters(() => ({ duration: label }))} />
      <PopularBlogs config={catalog.blogs} />
      <Awards />
      <TourReviews config={catalog.reviews} />

      {/* FAQ */}
      <section className="py-16 bg-white" data-testid="section-faq">
        <div className="max-w-3xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-9">
            <h2 className="font-serif-display text-3xl md:text-[38px] text-[var(--navy)]">
              Frequently Asked <span className="text-[var(--yellow-2)]">Questions</span>
            </h2>
            <p className="text-gray-500 mt-2">We help you prepare for your trip and ensure an effortless and enjoyable travel experience.</p>
          </div>
          <Accordion type="single" collapsible defaultValue="item-0" className="space-y-3">
            {(catalog.faqs || INDIA_FAQS).map((f, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="border border-gray-200 rounded-lg px-4 data-[state=open]:bg-[#f0f6ff] data-[state=open]:border-[#0b2545]/20">
                <AccordionTrigger className="text-[15px] font-semibold text-[var(--navy)] hover:no-underline text-left">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[14px] text-gray-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <Footer />
      <FloatingWidgets />
    </div>
  );
}
